/**
 * OpenWorldManager - Verwaltet das Open World Gameplay in Baden-Württemberg
 * 
 * Features:
 * - Freies Fahren durch BW
 * - Verkehrssystem mit KI-Fahrzeugen
 * - Wetter-System
 * - Tag/Nacht-Zyklus
 * - GPS-Navigation
 * - Polizei-System mit Geschwindigkeitskontrollen
 * - Realistische Fahrphysik mit Höhenprofil
 */

import * as THREE from 'three';
import { OpenWorldMapLoader, BW_CITIES, RoadType } from './OpenWorldMapLoader';
import { RealisticDrivingPhysics, type DrivingState, type PhysicsModifiers } from './RealisticDrivingPhysics';
import { TrafficCarModelLoader } from '../game/npc/TrafficCarModel';
import { VegetationSystem } from '../game/environment/VegetationSystem';
import { EnvironmentMapLoader } from '../game/loaders/EnvironmentMapLoader';
import { AssetDistributionSystem } from './AssetDistributionSystem';
import { RoadColliderManager } from './RoadColliderManager';

// Verkehrsteilnehmer
export interface TrafficVehicle {
  id: string;
  mesh: THREE.Object3D;
  position: THREE.Vector3;
  direction: THREE.Vector3;
  speed: number;
  targetSpeed: number;
  type: 'car' | 'truck' | 'bus' | 'motorcycle';
  color: THREE.Color;
}

// Wetter-Typen
export type WeatherType = 'sunny' | 'cloudy' | 'rainy' | 'foggy' | 'snowy';

export interface WeatherState {
  type: WeatherType;
  intensity: number; // 0-1
  windDirection: THREE.Vector3;
  windSpeed: number;
  visibility: number;
}

// GPS Wegpunkt
export interface GPSWaypoint {
  position: THREE.Vector3;
  name: string;
  type: 'city' | 'poi' | 'checkpoint';
}

// Spieler-Status - erweitert mit Fahrphysik
export interface PlayerStatus {
  position: THREE.Vector3;
  speed: number;
  onRoad: boolean;
  currentRoadType?: RoadType;
  speedLimit?: number;
  isOverspeed: boolean;
  wantedLevel: number; // 0-5
  nearbyRoads?: Array<{ points: Array<{ x: number; z: number }> }>;
  gpsLocation: {
    lat: number;
    lon: number;
    cityName: string | null;
    roadName?: string;
    roadType?: RoadType;
  };
  // Erweiterte Fahrphysik
  drivingState?: DrivingState;
  physicsModifiers?: PhysicsModifiers;
}

export class OpenWorldManager {
  private scene: THREE.Scene;
  private mapLoader: OpenWorldMapLoader;
  private environmentMapLoader: EnvironmentMapLoader;
  private environmentMapGroup: THREE.Group | null = null;
  
  // Realistische Fahrphysik
  private drivingPhysics: RealisticDrivingPhysics;
  
  // Verkehr
  private trafficVehicles: Map<string, TrafficVehicle> = new Map();
  private maxTrafficVehicles = 30;
  private trafficGroup: THREE.Group;
  private trafficModelLoader: TrafficCarModelLoader;
  private trafficModelsLoaded = 0;
  
  // Vegetation & Umwelt
  private vegetationSystem: VegetationSystem;
  private assetDistribution: AssetDistributionSystem | null = null;
  private roadDecorTick = 0;
  private minimapRoadTick = 0;
  private cachedMinimapRoads: Array<{ points: Array<{ x: number; z: number }> }> = [];
  private guardrailRoads = new Set<string>();
  private signRoads = new Set<string>();
  private citySigns = new Set<string>(); // Track which cities have had signs spawned
  private lastCityName: string | null = null;
  
  // Physische Collider-Barrieren
  private colliderManager: RoadColliderManager;
  
  // Wetter
  private currentWeather: WeatherState = {
    type: 'sunny',
    intensity: 0,
    windDirection: new THREE.Vector3(1, 0, 0),
    windSpeed: 5,
    visibility: 10000
  };
  private weatherParticles: THREE.Points | null = null;
  private fog: THREE.Fog | null = null;
  
  // Tag/Nacht
  private timeOfDay = 12; // 0-24 Stunden
  private dayNightSpeed = 1; // 1 = Echtzeit, 60 = 1h = 1min
  private sun: THREE.DirectionalLight;
  private ambient: THREE.AmbientLight;
  private skyColor = new THREE.Color(0x87ceeb);
  
  // Polizei
  private policeActive = false;
  private lastSpeedingTime = 0;
  private speedingDuration = 0;
  
  // GPS
  private gpsDestination: GPSWaypoint | null = null;
  private gpsRoute: THREE.Vector3[] = [];
  private gpsLineMesh: THREE.Line | null = null;
  
  // Callbacks
  public onSpeedingDetected?: (overSpeed: number, duration: number) => void;
  public onWantedLevelChanged?: (level: number) => void;
  public onWeatherChanged?: (weather: WeatherState) => void;
  public onCityEntered?: (cityName: string) => void;
  
  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.mapLoader = new OpenWorldMapLoader(scene);
    this.environmentMapLoader = new EnvironmentMapLoader();
    
    // Realistische Fahrphysik initialisieren
    this.drivingPhysics = new RealisticDrivingPhysics();
    
    // Traffic Model Loader
    this.trafficModelLoader = new TrafficCarModelLoader();
    
    // Traffic Group
    this.trafficGroup = new THREE.Group();
    this.trafficGroup.name = 'TrafficVehicles';
    this.scene.add(this.trafficGroup);
    
    // Vegetation System initialisieren
    this.vegetationSystem = new VegetationSystem();
    this.scene.add(this.vegetationSystem.getGroup());
    
    // Collider Manager initialisieren (Debug-Modus aus)
    this.colliderManager = new RoadColliderManager(scene, false);
    
    // Asset Distribution System initialisieren
    this.assetDistribution = new AssetDistributionSystem(this.scene, {
      maxBuildings: 500,
      maxProps: 2000,
      maxVegetation: 5000,
      buildingDensity: 0.7,
      propDensity: 0.5,
      vegetationDensity: 0.3,
      loadRadius: 500
    });
    
    // Beleuchtung
    this.sun = new THREE.DirectionalLight(0xffffee, 1);
    this.sun.position.set(100, 200, 100);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.width = 2048;
    this.sun.shadow.mapSize.height = 2048;
    this.sun.shadow.camera.near = 10;
    this.sun.shadow.camera.far = 500;
    this.sun.shadow.camera.left = -200;
    this.sun.shadow.camera.right = 200;
    this.sun.shadow.camera.top = 200;
    this.sun.shadow.camera.bottom = -200;
    this.scene.add(this.sun);
    
    this.ambient = new THREE.AmbientLight(0x404050, 0.5);
    this.scene.add(this.ambient);
    
    // Fog für Atmosphäre
    this.fog = new THREE.Fog(0x87ceeb, 1000, 10000);
    this.scene.fog = this.fog;
  }
  
  /**
   * Initialisiert die Open World bei einer Stadt
   */
  initialize(startCity: keyof typeof BW_CITIES = 'stuttgart'): THREE.Vector3 {
    const startPos = this.mapLoader.teleportToCity(startCity);
    
    // Spawne initial Traffic im Hintergrund
    this.spawnInitialTraffic(startPos).catch(err => console.error('Traffic spawn failed:', err));

    // Lade große Umgebungsmap im Hintergrund
    this.loadEnvironmentMap().catch(err => console.error('Environment map load failed:', err));
    
    // Vegetation spawnen in der Umgebung (intensiv)
    this.vegetationSystem.spawnVegetationArea(startPos, 400, 60);
    
    // Verkehrsschilder spawnen (viele Positionen für bessere Orientierung)
    const signPositions = [];
    for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 6) {
      for (let dist = 40; dist < 250; dist += 60) {
        const signTypes: Array<'speed' | 'stop' | 'yield' | 'parking'> = ['speed', 'stop', 'yield', 'parking'];
        const type = signTypes[Math.floor(Math.random() * signTypes.length)];
        signPositions.push({
          pos: new THREE.Vector3(
            startPos.x + Math.cos(angle) * dist,
            0,
            startPos.z + Math.sin(angle) * dist
          ),
          type
        });
      }
    }
    this.vegetationSystem.spawnTrafficSigns(signPositions);
    
    // Ampeln an Kreuzungen (mehr Kreuzungen)
    const intersections = [];
    for (let i = -2; i <= 2; i++) {
      for (let j = -2; j <= 2; j++) {
        if (i === 0 && j === 0) continue;
        intersections.push(new THREE.Vector3(startPos.x + i * 120, 0, startPos.z + j * 120));
      }
    }
    this.vegetationSystem.spawnTrafficLights(intersections);
    
    // Setze Startzeit auf Mittag
    this.setTimeOfDay(12);
    
    return startPos;
  }
  
  /**
   * Spawnt initiale Verkehrsteilnehmer
   */
  private async spawnInitialTraffic(aroundPosition: THREE.Vector3): Promise<void> {
    const promises: Promise<TrafficVehicle | null>[] = [];
    
    for (let i = 0; i < 10; i++) {
      const angle = (i / 10) * Math.PI * 2;
      const distance = 50 + Math.random() * 200;
      
      const position = new THREE.Vector3(
        aroundPosition.x + Math.cos(angle) * distance,
        0,
        aroundPosition.z + Math.sin(angle) * distance
      );
      
      promises.push(this.spawnTrafficVehicle(position));
    }
    
    await Promise.allSettled(promises);
  }
  
  /**
   * Spawnt ein Verkehrsfahrzeug mit GLB-Modellen
   */
  private async spawnTrafficVehicle(position: THREE.Vector3): Promise<TrafficVehicle | null> {
    if (this.trafficVehicles.size >= this.maxTrafficVehicles) {
      return null;
    }
    
    const types: Array<'car' | 'truck' | 'bus' | 'motorcycle'> = ['car', 'car', 'car', 'truck', 'bus', 'motorcycle'];
    const type = types[Math.floor(Math.random() * types.length)];
    
    let mesh: THREE.Object3D;
    let color: THREE.Color;
    
    // Versuche GLB-Modelle zu laden
    try {
      const modelIds = [
        'traffic_mini',
        'traffic_ford',
        'traffic_dodge',
        'traffic_truck',
        'traffic_bus',
        'traffic_motorcycle',
        'traffic_indian_pack',
        'traffic_generic_pack'
      ] as const;
      const modelId = modelIds[Math.floor(Math.random() * modelIds.length)];
      
      const glbModel = await this.trafficModelLoader.loadModel(modelId);
      mesh = glbModel;
      color = new THREE.Color(1, 1, 1);
    } catch (error) {
      // Fallback zu BoxGeometry wenn GLB-Loading fehlschlägt
      console.warn('GLB-Loading fehlgeschlagen, verwende BoxGeometry Fallback', error);
      
      let geometry: THREE.BufferGeometry;
      
      switch (type) {
        case 'truck':
          geometry = new THREE.BoxGeometry(3, 3.5, 8);
          color = new THREE.Color(0.3, 0.3, 0.35);
          break;
        case 'bus':
          geometry = new THREE.BoxGeometry(2.5, 3, 12);
          color = new THREE.Color(0.8, 0.6, 0.1);
          break;
        case 'motorcycle':
          geometry = new THREE.BoxGeometry(0.8, 1.2, 2.2);
          color = new THREE.Color(0.2 + Math.random() * 0.8, 0.1, 0.1);
          break;
        default: // car
          geometry = new THREE.BoxGeometry(1.8, 1.4, 4.2);
          color = new THREE.Color().setHSL(Math.random(), 0.6, 0.5);
      }
      
      const material = new THREE.MeshStandardMaterial({ color, roughness: 0.5, metalness: 0.5 });
      mesh = new THREE.Mesh(geometry, material);
    }
    
    mesh.position.copy(position);
    mesh.position.y = type === 'truck' ? 1.75 : type === 'bus' ? 1.5 : type === 'motorcycle' ? 0.6 : 0.7;
    mesh.traverse(child => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
    
    const id = `traffic_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const vehicle: TrafficVehicle = {
      id,
      mesh,
      position: position.clone(),
      direction: new THREE.Vector3(Math.random() - 0.5, 0, Math.random() - 0.5).normalize(),
      speed: 0,
      targetSpeed: 30 + Math.random() * 40,
      type,
      color
    };
    
    mesh.lookAt(position.clone().add(vehicle.direction));
    
    this.trafficVehicles.set(id, vehicle);
    this.trafficGroup.add(mesh);
    
    return vehicle;
  }
  
  /**
   * Aktualisiert alle Verkehrsteilnehmer
   */
  private updateTraffic(dt: number, playerPosition: THREE.Vector3): void {
    for (const [id, vehicle] of this.trafficVehicles.entries()) {
      // Straßen-Snapping für realistisches Fahren
      const roadSnap = this.mapLoader.getNearestRoadPoint(vehicle.position, 15);
      if (roadSnap) {
        const snapOffset = new THREE.Vector3().subVectors(roadSnap.point, vehicle.position);
        const roadWidth = roadSnap.roadWidth ?? 6;

        if (roadSnap.direction) {
          const right = new THREE.Vector3(roadSnap.direction.z, 0, -roadSnap.direction.x).normalize();
          const lateral = snapOffset.dot(right);
          const maxLateral = Math.max(1.6, roadWidth * 0.5);
          const over = Math.abs(lateral) - maxLateral;
          if (over > 0) {
            const correction = right.multiplyScalar(over * Math.sign(lateral));
            vehicle.position.add(correction);
          }
          vehicle.direction.lerp(roadSnap.direction, Math.min(1, dt * 4));
        } else {
          const snapDist = snapOffset.length();
          if (snapDist > roadWidth * 0.6) {
            vehicle.position.copy(roadSnap.point);
          } else if (snapDist > 3) {
            vehicle.position.add(snapOffset.multiplyScalar(Math.min(1, dt * 2)));
          }
          const roadPoints = this.findRoadPointsAhead(vehicle.position, roadSnap.roadType);
          if (roadPoints.length > 0) {
            const targetPoint = roadPoints[0];
            const roadDir = new THREE.Vector3().subVectors(targetPoint, vehicle.position).normalize();
            vehicle.direction.lerp(roadDir, Math.min(1, dt * 3));
          }
        }

        // Geschwindigkeitslimit beachten
        if (roadSnap.speedLimit) {
          vehicle.targetSpeed = Math.min(vehicle.targetSpeed, roadSnap.speedLimit + 5);
        }
      } else {
        // Außerhalb Straße: langsam zurück zur Straße
        vehicle.speed *= 0.95;
      }
      
      // Kollisionsvermeidung
      let minDistanceToOther = Infinity;
      for (const [otherId, otherVehicle] of this.trafficVehicles.entries()) {
        if (id === otherId) continue;
        const distance = vehicle.position.distanceTo(otherVehicle.position);
        if (distance < minDistanceToOther) {
          minDistanceToOther = distance;
        }
        if (distance < 12) {
          vehicle.targetSpeed = Math.max(10, Math.min(vehicle.targetSpeed, otherVehicle.speed - 8));
        }
      }
      
      // Beschleunigen/Bremsen
      if (vehicle.speed < vehicle.targetSpeed) {
        vehicle.speed = Math.min(vehicle.speed + 15 * dt, vehicle.targetSpeed);
      } else if (vehicle.speed > vehicle.targetSpeed) {
        vehicle.speed = Math.max(vehicle.speed - 25 * dt, vehicle.targetSpeed);
      }
      
      // Bewegung
      const moveDistance = vehicle.speed * dt * 0.277;
      vehicle.position.addScaledVector(vehicle.direction, moveDistance);
      
      // Korrekte Positionierung auf Straße (Y-Achse)
      const roadInfo = this.mapLoader.isOnRoad(vehicle.position);
      vehicle.position.y = roadInfo.onRoad ? 0.1 : vehicle.position.y;
      
      vehicle.mesh.position.copy(vehicle.position);
      const heightOffset = vehicle.type === 'truck' ? 1.75 : vehicle.type === 'bus' ? 1.5 : vehicle.type === 'motorcycle' ? 0.6 : 0.7;
      vehicle.mesh.position.y = vehicle.position.y + heightOffset;
      vehicle.mesh.lookAt(vehicle.position.clone().add(vehicle.direction));
      
      // Entferne Fahrzeuge die zu weit weg sind
      const distToPlayer = vehicle.position.distanceTo(playerPosition);
      if (distToPlayer > 500) {
        this.trafficGroup.remove(vehicle.mesh);
        this.disposeObject3D(vehicle.mesh);
        this.trafficVehicles.delete(id);
      }
    }
    
    // Spawne neue Fahrzeuge wenn zu wenige
    if (this.trafficVehicles.size < this.maxTrafficVehicles * 0.7) {
      const spawnAngle = Math.random() * Math.PI * 2;
      const spawnDist = 200 + Math.random() * 100;
      const spawnPos = new THREE.Vector3(
        playerPosition.x + Math.cos(spawnAngle) * spawnDist,
        0,
        playerPosition.z + Math.sin(spawnAngle) * spawnDist
      );
      this.spawnTrafficVehicle(spawnPos);
    }
  }

  private updateRoadDecor(playerPosition: THREE.Vector3): void {
    const nearbyRoads = this.mapLoader.getRoadsInRadius(playerPosition, 260);
    const guardrailTypes: RoadType[] = ['motorway', 'trunk', 'primary', 'secondary', 'tertiary'];

    const placeAlongPolyline = (
      pts: THREE.Vector3[],
      spacing: number,
      cb: (pos: THREE.Vector3, dir: THREE.Vector3) => void
    ) => {
      if (pts.length < 2) return;
      let carry = 0;
      for (let i = 1; i < pts.length; i += 1) {
        const a = pts[i - 1];
        const b = pts[i];
        const seg = b.clone().sub(a);
        const len = seg.length();
        if (len < 1e-3) continue;
        const dir = seg.clone().multiplyScalar(1 / len);

        let t = spacing - carry;
        while (t < len) {
          const p = a.clone().addScaledVector(dir, t);
          cb(p, dir);
          t += spacing;
        }
        carry = Math.max(0, t - len);
      }
    };

    for (const road of nearbyRoads) {
      if (guardrailTypes.includes(road.type) && !this.guardrailRoads.has(road.id)) {
        const offset = road.width * 0.7 + 1.2;
        const left = this.offsetPolyline(road.points, offset);
        const right = this.offsetPolyline(road.points, -offset);
        this.vegetationSystem.spawnGuardrailsAlongRoad(left);
        this.vegetationSystem.spawnGuardrailsAlongRoad(right);
        this.guardrailRoads.add(road.id);
      }

      if (!this.signRoads.has(road.id)) {
        const signPositions: Array<{ pos: THREE.Vector3; type: 'stop' | 'speed' | 'yield' | 'parking' }> = [];
        placeAlongPolyline(road.points, 90, (pos, dir) => {
          const right = new THREE.Vector3(dir.z, 0, -dir.x);
          const signPos = pos.clone().addScaledVector(right, road.width * 0.6 + 1.4);
          let type: 'stop' | 'speed' | 'yield' | 'parking' = 'yield';
          if (road.type === 'motorway' || road.type === 'trunk' || road.type === 'primary') type = 'speed';
          if (road.type === 'residential') type = 'stop';
          signPositions.push({ pos: signPos, type });
        });
        if (signPositions.length > 0) {
          this.vegetationSystem.spawnTrafficSigns(signPositions);
        }
        this.signRoads.add(road.id);
      }
    }
    
    // Update Collider für sichtbare Straßen
    this.colliderManager.updateCollidersForRoads(nearbyRoads);
  }

  private updateMinimapRoads(playerPosition: THREE.Vector3): void {
    const nearbyRoads = this.mapLoader.getRoadsInRadius(playerPosition, 320);
    this.cachedMinimapRoads = nearbyRoads.map((road) => {
      const sampled: Array<{ x: number; z: number }> = [];
      for (let i = 0; i < road.points.length; i += 2) {
        const p = road.points[i];
        sampled.push({ x: p.x, z: p.z });
      }
      const last = road.points[road.points.length - 1];
      if (last) sampled.push({ x: last.x, z: last.z });
      return { points: sampled };
    });
  }

  private offsetPolyline(points: THREE.Vector3[], offset: number): THREE.Vector3[] {
    if (points.length < 2) return points;
    const result: THREE.Vector3[] = [];
    for (let i = 0; i < points.length; i += 1) {
      const prev = points[Math.max(0, i - 1)];
      const next = points[Math.min(points.length - 1, i + 1)];
      const dir = new THREE.Vector3().subVectors(next, prev).normalize();
      const right = new THREE.Vector3(dir.z, 0, -dir.x);
      result.push(points[i].clone().addScaledVector(right, offset));
    }
    return result;
  }
  
  /**
   * Setzt die Tageszeit (0-24)
   */
  setTimeOfDay(hour: number): void {
    this.timeOfDay = hour % 24;
    this.updateDayNightCycle();
  }
  
  /**
   * Aktualisiert Tag/Nacht-Zyklus
   */
  private updateDayNightCycle(): void {
    const hour = this.timeOfDay;
    
    // Sonnenposition (vereinfacht)
    const sunAngle = ((hour - 6) / 12) * Math.PI; // 6 Uhr = Sonnenaufgang
    const sunHeight = Math.sin(sunAngle);
    
    if (sunHeight > 0) {
      // Tag
      this.sun.position.set(
        Math.cos(sunAngle) * 200,
        sunHeight * 200 + 50,
        100
      );
      
      // Lichtintensität
      const intensity = Math.min(1, sunHeight * 1.5);
      this.sun.intensity = intensity;
      this.ambient.intensity = 0.3 + intensity * 0.3;
      
      // Himmelfarbe
      const warmth = hour < 12 ? (hour - 6) / 6 : (18 - hour) / 6;
      if (warmth < 0.3) {
        // Morgen/Abenddämmerung
        this.skyColor.setHSL(0.05, 0.6, 0.5 + warmth);
        this.sun.color.setHSL(0.08, 0.8, 0.6);
      } else {
        // Taghimmel
        this.skyColor.setHSL(0.58, 0.5, 0.7);
        this.sun.color.setHSL(0.12, 0.2, 1);
      }
    } else {
      // Nacht
      this.sun.intensity = 0.05;
      this.ambient.intensity = 0.15;
      this.skyColor.setHSL(0.65, 0.3, 0.1);
      this.sun.color.setHSL(0.65, 0.5, 0.3);
    }
    
    // Fog anpassen
    if (this.fog) {
      this.fog.color.copy(this.skyColor);
    }
    
    // Hintergrundfarbe
    this.scene.background = this.skyColor;
  }
  
  /**
   * Setzt das Wetter
   */
  setWeather(type: WeatherType, intensity: number = 0.5): void {
    this.currentWeather.type = type;
    this.currentWeather.intensity = Math.max(0, Math.min(1, intensity));
    
    // Entferne alte Partikel
    if (this.weatherParticles) {
      this.scene.remove(this.weatherParticles);
      this.weatherParticles.geometry.dispose();
      (this.weatherParticles.material as THREE.Material).dispose();
      this.weatherParticles = null;
    }
    
    switch (type) {
      case 'rainy':
        this.createRainParticles();
        this.currentWeather.visibility = 500 + (1 - intensity) * 500;
        break;
      case 'foggy':
        this.currentWeather.visibility = 100 + (1 - intensity) * 400;
        break;
      case 'snowy':
        this.createSnowParticles();
        this.currentWeather.visibility = 300 + (1 - intensity) * 700;
        break;
      case 'cloudy':
        this.currentWeather.visibility = 2000;
        this.ambient.intensity *= 0.7;
        break;
      default: // sunny
        this.currentWeather.visibility = 10000;
    }
    
    // Fog anpassen
    if (this.fog) {
      this.fog.near = 50;
      this.fog.far = this.currentWeather.visibility;
    }
    
    this.onWeatherChanged?.(this.currentWeather);
  }
  
  /**
   * Erstellt Regen-Partikel
   */
  private createRainParticles(): void {
    const count = 5000 * this.currentWeather.intensity;
    const positions = new Float32Array(count * 3);
    
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 200;
      positions[i * 3 + 1] = Math.random() * 100;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 200;
    }
    
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    
    const material = new THREE.PointsMaterial({
      color: 0xaaaacc,
      size: 0.22,
      transparent: true,
      opacity: 0.35
    });
    
    this.weatherParticles = new THREE.Points(geometry, material);
    this.scene.add(this.weatherParticles);
  }
  
  /**
   * Erstellt Schnee-Partikel
   */
  private createSnowParticles(): void {
    const count = 3000 * this.currentWeather.intensity;
    const positions = new Float32Array(count * 3);
    
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 200;
      positions[i * 3 + 1] = Math.random() * 100;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 200;
    }
    
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    
    const material = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 1.5,
      transparent: true,
      opacity: 0.8
    });
    
    this.weatherParticles = new THREE.Points(geometry, material);
    this.scene.add(this.weatherParticles);
  }
  
  /**
   * Aktualisiert Wetter-Partikel
   */
  private updateWeatherParticles(dt: number, playerPosition: THREE.Vector3): void {
    if (!this.weatherParticles) return;
    
    this.weatherParticles.position.set(playerPosition.x, 0, playerPosition.z);
    
    const positions = this.weatherParticles.geometry.getAttribute('position');
    const type = this.currentWeather.type;
    const fallSpeed = type === 'rainy' ? 85 : 5; // Regen fällt schneller
    
    for (let i = 0; i < positions.count; i++) {
      let y = positions.getY(i);
      y -= fallSpeed * dt;
      
      // Wind drift for rain
      if (type === 'rainy') {
        const wind = this.currentWeather.windDirection;
        positions.setX(i, positions.getX(i) + wind.x * this.currentWeather.windSpeed * dt * 0.6);
        positions.setZ(i, positions.getZ(i) + wind.z * this.currentWeather.windSpeed * dt * 0.6);
      }
      
      // Schneeflocken bewegen sich auch seitlich
      if (type === 'snowy') {
        const x = positions.getX(i) + Math.sin(Date.now() * 0.001 + i) * dt * 2;
        const z = positions.getZ(i) + Math.cos(Date.now() * 0.001 + i) * dt * 2;
        positions.setX(i, x);
        positions.setZ(i, z);
      }
      
      if (y < 0) {
        y = 100;
        positions.setX(i, (Math.random() - 0.5) * 200);
        positions.setZ(i, (Math.random() - 0.5) * 200);
      }
      
      positions.setY(i, y);
    }
    
    positions.needsUpdate = true;
  }
  
  /**
   * Setzt ein GPS-Ziel
   */
  setGPSDestination(cityKey: keyof typeof BW_CITIES): void {
    const city = BW_CITIES[cityKey];
    const worldPos = this.mapLoader.gpsToWorld(city.lat, city.lon);
    
    this.gpsDestination = {
      position: worldPos,
      name: city.name,
      type: 'city'
    };
    
    // Erstelle Route-Linie (vereinfacht - gerade Linie)
    this.updateGPSRoute(new THREE.Vector3(0, 0, 0));
  }
  
  /**
   * Aktualisiert GPS-Route
   */
  private updateGPSRoute(playerPosition: THREE.Vector3): void {
    if (!this.gpsDestination) return;
    
    // Entferne alte Linie
    if (this.gpsLineMesh) {
      this.scene.remove(this.gpsLineMesh);
      this.gpsLineMesh.geometry.dispose();
      (this.gpsLineMesh.material as THREE.Material).dispose();
    }
    
    // Erstelle neue Linie
    const points = [
      new THREE.Vector3(playerPosition.x, 2, playerPosition.z),
      new THREE.Vector3(this.gpsDestination.position.x, 2, this.gpsDestination.position.z)
    ];
    
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({
      color: 0x00ff00,
      linewidth: 3,
      transparent: true,
      opacity: 0.7
    });
    
    this.gpsLineMesh = new THREE.Line(geometry, material);
    this.scene.add(this.gpsLineMesh);
  }
  
  /**
   * Prüft Geschwindigkeitsüberschreitung
   */
  private checkSpeeding(playerSpeed: number, speedLimit: number | undefined, dt: number): boolean {
    if (!speedLimit) return false;
    
    const overSpeed = playerSpeed - speedLimit;
    
    if (overSpeed > 5) { // 5 km/h Toleranz
      this.speedingDuration += dt;
      
      if (this.speedingDuration > 2) { // 2 Sekunden Speeding
        this.onSpeedingDetected?.(overSpeed, this.speedingDuration);
        return true;
      }
    } else {
      this.speedingDuration = Math.max(0, this.speedingDuration - dt * 2);
    }
    
    return false;
  }
  
  /**
   * Erhöht Wanted-Level
   */
  private increaseWantedLevel(currentLevel: number): number {
    const newLevel = Math.min(5, currentLevel + 1);
    this.onWantedLevelChanged?.(newLevel);
    return newLevel;
  }
  
  /**
   * Haupt-Update
   */
  update(dt: number, playerPosition: THREE.Vector3, playerSpeed: number, playerDirection?: THREE.Vector3): PlayerStatus {
    // Karte aktualisieren
    this.mapLoader.update(playerPosition);
    this.mapLoader.updateAnimations(dt);

    this.roadDecorTick += dt;
    if (this.roadDecorTick >= 3) {
      this.roadDecorTick = 0;
      this.updateRoadDecor(playerPosition);
    }

    this.minimapRoadTick += dt;
    if (this.minimapRoadTick >= 1.2) {
      this.minimapRoadTick = 0;
      this.updateMinimapRoads(playerPosition);
    }
    
    // Verkehr aktualisieren
    this.updateTraffic(dt, playerPosition);
    
    // Tag/Nacht-Zyklus
    this.timeOfDay += dt * (this.dayNightSpeed / 3600); // Sekunden zu Stunden
    if (this.timeOfDay >= 24) this.timeOfDay -= 24;
    this.updateDayNightCycle();
    
    // Wetter-Partikel
    this.updateWeatherParticles(dt, playerPosition);
    
    // GPS aktualisieren
    if (this.gpsDestination) {
      this.updateGPSRoute(playerPosition);
    }
    
    // Straßen-Check
    const roadInfo = this.mapLoader.isOnRoad(playerPosition);
    
    // Geschwindigkeits-Check
    const isOverspeed = this.checkSpeeding(playerSpeed, roadInfo.speedLimit, dt);
    
    // GPS-Position
    const baseGps = this.mapLoader.getCurrentGPS(playerPosition);
    const gpsLocation: PlayerStatus['gpsLocation'] = {
      ...baseGps
    };
    
    // Straßennamen und Typ hinzufügen
    if (roadInfo.onRoad && roadInfo.roadName) {
      gpsLocation.roadName = roadInfo.roadName;
      gpsLocation.roadType = roadInfo.roadType;
    }
    
    // Stadt-Eintritt prüfen
    if (gpsLocation.cityName) {
      this.onCityEntered?.(gpsLocation.cityName);
      
      // City Signs spawnen wenn in neue Stadt eintritt
      if (gpsLocation.cityName !== this.lastCityName) {
        this.lastCityName = gpsLocation.cityName;
        if (!this.citySigns.has(gpsLocation.cityName)) {
          this.vegetationSystem.spawnCitySigns(gpsLocation.cityName, playerPosition.clone());
          this.citySigns.add(gpsLocation.cityName);
        }
      }
    }
    
    // Realistische Fahrphysik aktualisieren
    const physicsModifiers = this.drivingPhysics.update(
      dt,
      playerPosition,
      gpsLocation.lat,
      gpsLocation.lon,
      playerSpeed,
      roadInfo.roadType,
      this.currentWeather.type,
      0, // throttle - TODO: Get from input
      0, // steeringAngle - TODO: Get from CarController
      false // isDrifting - TODO: Get from DriftBoostSystem
    );
    const currentDrivingState = this.drivingPhysics.getState();
    
    return {
      position: playerPosition,
      speed: playerSpeed,
      onRoad: roadInfo.onRoad,
      currentRoadType: roadInfo.roadType,
      speedLimit: roadInfo.speedLimit,
      isOverspeed,
      wantedLevel: 0, // TODO: Wanted-System
      nearbyRoads: this.cachedMinimapRoads,
      gpsLocation,
      drivingState: currentDrivingState,
      physicsModifiers
    };
  }
  
  /**
   * Gibt aktuelle Wetter-Info zurück
   */
  getWeather(): WeatherState {
    return { ...this.currentWeather };
  }
  
  /**
   * Gibt aktuelle Tageszeit zurück
   */
  getTimeOfDay(): number {
    return this.timeOfDay;
  }
  
  /**
   * Formatierte Uhrzeit
   */
  getFormattedTime(): string {
    const hours = Math.floor(this.timeOfDay);
    const minutes = Math.floor((this.timeOfDay % 1) * 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }
  
  /**
   * Gibt Map-Loader zurück für direkten Zugriff
   */
  getMapLoader(): OpenWorldMapLoader {
    return this.mapLoader;
  }
  
  /**
   * Gibt Fahrphysik-System zurück
   */
  getDrivingPhysics(): RealisticDrivingPhysics {
    return this.drivingPhysics;
  }
  
  /**
   * Gibt aktuelle Oberflächenart zurück
   */
  getCurrentSurface(): string {
    const state = this.drivingPhysics.getState();
    return state.currentSurface || 'asphalt';
  }
  
  /**
   * Gibt aktuelle Höhe über NN zurück
   */
  getCurrentElevation(): number {
    const state = this.drivingPhysics.getState();
    return state.elevation || 0;
  }

  /**
   * Lädt und zeigt eine Umgebungsmap (z.B. NFS-U2 City)
   */
  async loadEnvironmentMap(mapName: string = 'nfs-u2-city'): Promise<void> {
    try {
      console.log(`Lade Umgebungsmap: ${mapName}...`);
      
      const mapConfig = {
        name: mapName,
        modelPath: `/assets/car-race/maps/source/u2bw1.glb`,
        texturesPath: `/assets/car-race/maps/textures`,
        scale: 1.0
      };
      
      const mapGroup = await this.environmentMapLoader.loadMap(mapConfig);
      
      // Entferne alte Map wenn vorhanden
      if (this.environmentMapGroup) {
        this.scene.remove(this.environmentMapGroup);
      }
      
      this.environmentMapGroup = mapGroup;
      this.scene.add(mapGroup);
      
      console.log(`Umgebungsmap ${mapName} erfolgreich geladen`);
    } catch (error) {
      console.error(`Fehler beim Laden der Umgebungsmap ${mapName}:`, error);
      throw error;
    }
  }

  /**
   * Unlädt die aktuelle Umgebungsmap
   */
  unloadEnvironmentMap(): void {
    if (this.environmentMapGroup) {
      this.scene.remove(this.environmentMapGroup);
      this.environmentMapGroup.traverse(child => {
        if (child instanceof THREE.Mesh) {
          child.geometry?.dispose();
          if (Array.isArray(child.material)) {
            child.material.forEach(m => m.dispose());
          } else {
            child.material?.dispose();
          }
        }
      });
      this.environmentMapGroup = null;
    }
  }

  private disposeObject3D(object: THREE.Object3D): void {
    object.traverse(child => {
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach(material => material.dispose());
        } else {
          child.material?.dispose();
        }
      }
    });
  }

  /**
   * Findet Straßenpunkte voraus für NPC-Richtung
   */
  private findRoadPointsAhead(position: THREE.Vector3, roadType?: RoadType): THREE.Vector3[] {
    const basePoint = this.mapLoader.getNearestRoadPoint(position, 20);
    if (!basePoint) return [];
    
    const ahead = new THREE.Vector3(
      basePoint.point.x + Math.random() * 40 - 20,
      0,
      basePoint.point.z + Math.random() * 40 - 20
    );
    
    const targetSnap = this.mapLoader.getNearestRoadPoint(ahead, 30);
    if (targetSnap && targetSnap.roadType === roadType) {
      return [targetSnap.point];
    }
    return [basePoint.point];
  }
  
  /**
   * Getter für ColliderManager
   */
  getColliderManager(): RoadColliderManager {
    return this.colliderManager;
  }

  /**
   * Dispose
   */
  dispose(): void {
    // Verkehr
    for (const vehicle of this.trafficVehicles.values()) {
      this.trafficGroup.remove(vehicle.mesh);
      this.disposeObject3D(vehicle.mesh);
    }
    this.scene.remove(this.trafficGroup);
    
    // Wetter
    if (this.weatherParticles) {
      this.scene.remove(this.weatherParticles);
      this.weatherParticles.geometry.dispose();
      (this.weatherParticles.material as THREE.Material).dispose();
    }
    
    // GPS
    if (this.gpsLineMesh) {
      this.scene.remove(this.gpsLineMesh);
      this.gpsLineMesh.geometry.dispose();
      (this.gpsLineMesh.material as THREE.Material).dispose();
    }
    
    // Lichter
    this.scene.remove(this.sun);
    this.scene.remove(this.ambient);
    
    // Environment Map
    this.unloadEnvironmentMap();
    this.environmentMapLoader.dispose();
    
    // Collider Manager
    this.colliderManager.dispose();
    
    // Map
    this.mapLoader.dispose();
  }
}

export default OpenWorldManager;
